import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Chip,
  Card,
  CardContent,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  Rating,
  useTheme,
  useMediaQuery,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Send,
  Lightbulb,
  BugReport,
  Speed,
  Accessibility,
  Star,
  Refresh,
  Edit,
  Save,
  Cancel,
} from "@mui/icons-material";
import { improvementRequestsService, usersService } from "@catalog/supabase";
import { useAuth } from "../contexts/AuthContext";

interface ImprovementRequest {
  title: string;
  description: string;
  category: string;
  priority: number;
  userEmail: string;
}

interface RequestData {
  id: string;
  title: string;
  description: string;
  category: string;
  priority: number;
  user_email: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  updated_at: string;
}

const RequestImprovementPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState<ImprovementRequest>({
    title: "",
    description: "",
    category: "",
    priority: 3,
    userEmail: user?.email || "",
  });

  const [allRequests, setAllRequests] = useState<RequestData[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [userRole, setUserRole] = useState<
    "admin" | "editor" | "viewer" | null
  >(null);
  const [editingRequest, setEditingRequest] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<string>("");
  const [editingNotes, setEditingNotes] = useState<string>("");

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const categories = [
    { value: "feature", label: "New Feature", icon: <Lightbulb /> },
    { value: "bug", label: "Bug Fix", icon: <BugReport /> },
    { value: "performance", label: "Performance", icon: <Speed /> },
    { value: "accessibility", label: "Accessibility", icon: <Accessibility /> },
    { value: "ui", label: "User Interface", icon: <Star /> },
  ];

  // Fetch all requests on component mount
  useEffect(() => {
    fetchAllRequests();
  }, []);

  // Update form email when user changes
  useEffect(() => {
    if (user?.email && formData.userEmail !== user.email) {
      setFormData((prev) => ({
        ...prev,
        userEmail: user.email,
      }));
    }
  }, [user?.email]);

  // Fetch user role
  useEffect(() => {
    const fetchUserRole = async () => {
      if (isAuthenticated && user) {
        const role = await usersService.getCurrentUserRole();
        setUserRole(role);
        console.log("User role:", role);
      }
    };
    fetchUserRole();
  }, [isAuthenticated, user]);

  const fetchAllRequests = async () => {
    setLoadingRequests(true);
    try {
      console.log("Fetching all improvement requests...");
      console.log("Current user:", user?.email || "Not logged in");
      console.log("Is authenticated:", isAuthenticated);

      const requests = await improvementRequestsService.getAll();
      console.log("Fetched requests:", requests);

      // If no requests returned and user is authenticated, try fetching their own requests
      if (requests.length === 0 && isAuthenticated && user?.email) {
        console.log(
          "No requests from getAll(), trying to fetch user's own requests..."
        );
        const userRequests = await improvementRequestsService.getByUserEmail(
          user.email
        );
        console.log("User's own requests:", userRequests);
        setAllRequests(userRequests);

        setSnackbar({
          open: true,
          message: `Loaded ${userRequests.length} of your improvement requests`,
          severity: "success",
        });
      } else {
        setAllRequests(requests);
        setSnackbar({
          open: true,
          message: `Loaded ${requests.length} improvement requests`,
          severity: "success",
        });
      }
    } catch (error) {
      console.error("Error fetching requests:", error);
      setSnackbar({
        open: true,
        message: "Failed to load improvement requests. Please try again.",
        severity: "error",
      });
    } finally {
      setLoadingRequests(false);
    }
  };

  const getCategoryIcon = (category: string) => {
    const categoryMap = {
      feature: <Lightbulb />,
      bug: <BugReport />,
      performance: <Speed />,
      accessibility: <Accessibility />,
      ui: <Star />,
    };
    return categoryMap[category as keyof typeof categoryMap] || <Star />;
  };

  const getCategoryColor = (category: string) => {
    const colorMap = {
      feature: "#2196F3",
      bug: "#F44336",
      performance: "#FF9800",
      accessibility: "#9C27B0",
      ui: "#4CAF50",
    };
    return colorMap[category as keyof typeof colorMap] || "#666";
  };

  const getPriorityLabel = (priority: number) => {
    const labels = {
      1: "Low",
      2: "Low-Medium",
      3: "Medium",
      4: "High",
      5: "Critical",
    };
    return labels[priority as keyof typeof labels] || "Medium";
  };

  const getPriorityColor = (priority: number) => {
    const colors = {
      1: "#4CAF50",
      2: "#8BC34A",
      3: "#FF9800",
      4: "#FF5722",
      5: "#F44336",
    };
    return colors[priority as keyof typeof colors] || "#FF9800";
  };

  const getStatusColor = (status: string) => {
    const statusColors = {
      pending: "warning",
      under_review: "info",
      in_progress: "primary",
      completed: "success",
      rejected: "error",
      fixed: "success",
    };
    return statusColors[status as keyof typeof statusColors] || "default";
  };

  const handleInputChange = (field: keyof ImprovementRequest, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.title ||
      !formData.description ||
      !formData.category ||
      !formData.userEmail
    ) {
      setSnackbar({
        open: true,
        message: "Please fill in all required fields",
        severity: "error",
      });
      return;
    }

    try {
      // Save to database using the improvement requests service
      const result = await improvementRequestsService.create({
        title: formData.title,
        description: formData.description,
        category: formData.category as
          | "feature"
          | "bug"
          | "performance"
          | "accessibility"
          | "ui",
        priority: formData.priority,
        user_email: formData.userEmail,
      });

      if (result) {
        setSnackbar({
          open: true,
          message:
            "Your improvement request has been submitted successfully! We'll review it and get back to you.",
          severity: "success",
        });

        // Reset form
        setFormData({
          title: "",
          description: "",
          category: "",
          priority: 3,
          userEmail: user?.email || "",
        });

        // Refresh requests list
        fetchAllRequests();
      } else {
        throw new Error("Failed to create improvement request");
      }
    } catch (error) {
      console.error("Error submitting improvement request:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit request. Please try again.",
        severity: "error",
      });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const startEditing = (request: RequestData) => {
    setEditingRequest(request.id);
    setEditingStatus(request.status);
    setEditingNotes(request.admin_notes || "");
  };

  const cancelEditing = () => {
    setEditingRequest(null);
    setEditingStatus("");
    setEditingNotes("");
  };

  const saveEditing = async () => {
    if (!editingRequest) return;

    try {
      await improvementRequestsService.updateStatus(editingRequest, {
        status: editingStatus as any,
        admin_notes: editingNotes || null,
      });

      setSnackbar({
        open: true,
        message: "Request updated successfully!",
        severity: "success",
      });

      // Refresh the requests list
      fetchAllRequests();
      cancelEditing();
    } catch (error) {
      console.error("Error updating request:", error);
      setSnackbar({
        open: true,
        message: "Failed to update request. Please try again.",
        severity: "error",
      });
    }
  };

  return (
    <Box
      sx={{
        background: "transparent",
        minHeight: "100vh",
        pb: { xs: 2, md: 4 }, // Reduced mobile padding
      }}
    >
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 } }}>
        {" "}
        {/* Reduced mobile padding */}
        {/* Header */}
        <Box sx={{ mb: { xs: 3, md: 6 }, textAlign: "center" }}>
          {" "}
          {/* Reduced mobile margin */}
          <Typography
            variant="h3"
            sx={{
              mb: { xs: 1, md: 2 }, // Reduced mobile margin
              fontWeight: 700,
              fontSize: { xs: "1.75rem", md: "3rem" }, // Smaller font on mobile
              background: "linear-gradient(135deg, #1D1D1F 0%, #86868B 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Request Improvement
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontWeight: 400,
              fontSize: { xs: "1rem", md: "1.25rem" }, // Smaller font on mobile
            }}
          >
            Help us make the app better by sharing your ideas and feedback
          </Typography>
        </Box>
        {/* Improvement Categories */}
        <Grid
          container
          spacing={{ xs: 1.5, md: 2 }}
          sx={{ mb: { xs: 2.5, md: 4 } }}
        >
          {" "}
          {/* Reduced spacing and margin */}
          {categories.map((category) => (
            <Grid item xs={6} sm={6} md={4} key={category.value}>
              {" "}
              {/* Changed xs from 12 to 6 for 2 columns on mobile */}
              <Card
                sx={{
                  cursor: "pointer",
                  transition: "all 0.2s",
                  border: formData.category === category.value ? 2 : 1,
                  borderColor:
                    formData.category === category.value
                      ? "primary.main"
                      : "divider",
                  "&:hover": {
                    transform: { xs: "none", sm: "translateY(-2px)" }, // Disable hover effect on mobile
                    boxShadow: { xs: 1, sm: 2 },
                  },
                }}
                onClick={() => handleInputChange("category", category.value)}
              >
                <CardContent
                  sx={{
                    textAlign: "center",
                    py: { xs: 1.5, md: 2 }, // Reduced mobile padding
                    px: { xs: 1, md: 2 }, // Reduced mobile padding
                  }}
                >
                  <Box
                    sx={{
                      color: "primary.main",
                      mb: { xs: 0.5, md: 1 }, // Reduced mobile margin
                      "& svg": { fontSize: { xs: 20, md: 24 } }, // Smaller icons on mobile
                    }}
                  >
                    {category.icon}
                  </Box>
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }} // Smaller text on mobile
                  >
                    {category.label}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        {/* Request Form */}
        <Paper
          sx={{
            p: { xs: 2, md: 4 }, // Further reduced mobile padding
            borderRadius: { xs: 2, md: 3 }, // Smaller border radius on mobile
            boxShadow: 1,
          }}
        >
          <form onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 1.5, md: 3 }}>
              {" "}
              {/* Further reduced mobile spacing */}
              {/* Email */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Your Email"
                  type="email"
                  value={formData.userEmail}
                  onChange={(e) =>
                    handleInputChange("userEmail", e.target.value)
                  }
                  required
                  helperText="We'll use this to follow up on your request"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    },
                    "& .MuiFormHelperText-root": {
                      fontSize: { xs: "0.6875rem", md: "0.75rem" },
                    },
                  }}
                />
              </Grid>
              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Request Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  required
                  placeholder="Brief description of your improvement idea"
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    },
                  }}
                />
              </Grid>
              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Detailed Description"
                  multiline
                  rows={isMobile ? 3 : 6} // Fewer rows on mobile
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  required
                  placeholder="Please provide as much detail as possible about your improvement idea, including the problem it solves and how it would benefit users..."
                  sx={{
                    "& .MuiInputLabel-root": {
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    },
                    "& .MuiInputBase-input": {
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    },
                  }}
                />
              </Grid>
              {/* Priority */}
              <Grid item xs={12}>
                <Box>
                  <Typography
                    component="legend"
                    sx={{
                      mb: { xs: 0.5, md: 1 }, // Reduced mobile margin
                      fontSize: { xs: "0.875rem", md: "1rem" }, // Smaller text on mobile
                    }}
                  >
                    Priority Level
                  </Typography>
                  <Rating
                    name="priority"
                    value={formData.priority}
                    onChange={(event, newValue) => {
                      handleInputChange("priority", newValue || 1);
                    }}
                    max={5}
                    size={isMobile ? "medium" : "large"} // Smaller rating on mobile
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mt: { xs: 0.5, md: 1 }, // Reduced mobile margin
                      fontSize: { xs: "0.75rem", md: "0.875rem" }, // Smaller text on mobile
                    }}
                  >
                    {formData.priority === 1 && "Low priority - Nice to have"}
                    {formData.priority === 2 && "Low-Medium priority"}
                    {formData.priority === 3 && "Medium priority"}
                    {formData.priority === 4 && "High priority - Important"}
                    {formData.priority === 5 && "Critical - Urgently needed"}
                  </Typography>
                </Box>
              </Grid>
              {/* Submit Button */}
              <Grid item xs={12}>
                <Stack
                  direction={{ xs: "column", sm: "row" }} // Stack vertically on mobile
                  spacing={{ xs: 1, md: 2 }} // Reduced mobile spacing
                  justifyContent="flex-end"
                >
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={() => {
                      setFormData({
                        title: "",
                        description: "",
                        category: "",
                        priority: 3,
                        userEmail: "",
                      });
                    }}
                    sx={{
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      py: { xs: 1, md: 1.5 },
                      order: { xs: 2, sm: 1 }, // Submit button first on mobile
                    }}
                  >
                    Clear Form
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Send sx={{ fontSize: { xs: 18, md: 20 } }} />}
                    sx={{
                      background:
                        "linear-gradient(135deg, #007AFF 0%, #5AC8FA 100%)",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #0056CC 0%, #4A9FE7 100%)",
                      },
                      fontSize: { xs: "0.875rem", md: "1rem" },
                      py: { xs: 1, md: 1.5 },
                      order: { xs: 1, sm: 2 }, // Submit button first on mobile
                    }}
                  >
                    Submit Request
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Paper>
        {/* Help Text */}
        <Alert
          severity="info"
          sx={{
            mt: { xs: 2, md: 3 }, // Reduced mobile margin
            fontSize: { xs: "0.75rem", md: "0.875rem" }, // Smaller text on mobile
            "& .MuiAlert-icon": {
              fontSize: { xs: 18, md: 22 }, // Smaller icon on mobile
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "0.75rem", md: "0.875rem" } }} // Smaller text on mobile
          >
            <strong>Tips for better requests:</strong>
            <br />
            • Be specific about the problem you're trying to solve
            <br />
            • Explain how the improvement would benefit you and other users
            <br />
            • Include any relevant context or use cases
            <br />• If reporting a bug, include steps to reproduce it
          </Typography>
        </Alert>
        {/* Divider */}
        <Divider sx={{ my: 4 }} />
        {/* All Requests Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <Typography variant="h4" sx={{ flexGrow: 1 }}>
              All Improvement Requests
            </Typography>
            <Tooltip title="Refresh">
              <IconButton onClick={fetchAllRequests} disabled={loadingRequests}>
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>

          {loadingRequests ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>
                Loading improvement requests...
              </Typography>
            </Box>
          ) : allRequests.length === 0 ? (
            <Alert severity="info">No improvement requests found.</Alert>
          ) : (
            <Grid container spacing={2}>
              {allRequests.map((request) => (
                <Grid item xs={12} md={6} key={request.id}>
                  <Card
                    sx={{
                      height: "100%",
                      border: 1,
                      borderColor: "divider",
                      "&:hover": {
                        boxShadow: 2,
                      },
                      // Gray out fixed requests
                      ...(request.status === "fixed" && {
                        opacity: 0.6,
                        backgroundColor: "grey.100",
                        "& .MuiTypography-root": {
                          color: "text.disabled",
                        },
                        "& .MuiChip-root": {
                          opacity: 0.7,
                        },
                      }),
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{ display: "flex", alignItems: "center", mb: 2 }}
                      >
                        <Box
                          sx={{
                            color: getCategoryColor(request.category),
                            mr: 1,
                          }}
                        >
                          {getCategoryIcon(request.category)}
                        </Box>
                        <Typography
                          variant="h6"
                          component="h3"
                          sx={{ flexGrow: 1 }}
                        >
                          {request.title}
                        </Typography>
                        <Chip
                          label={getPriorityLabel(request.priority)}
                          size="small"
                          sx={{
                            backgroundColor: getPriorityColor(request.priority),
                            color: "white",
                          }}
                        />
                        {userRole === "admin" && (
                          <Tooltip title="Edit Request">
                            <IconButton
                              size="small"
                              onClick={() => startEditing(request)}
                              sx={{ ml: 1 }}
                              disabled={editingRequest === request.id}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {request.description}
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Chip
                          label={request.category}
                          size="small"
                          variant="outlined"
                          sx={{
                            borderColor: getCategoryColor(request.category),
                            color: getCategoryColor(request.category),
                          }}
                        />
                        {editingRequest === request.id ? (
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <Select
                              value={editingStatus}
                              onChange={(e) => setEditingStatus(e.target.value)}
                              displayEmpty
                            >
                              <MenuItem value="pending">Pending</MenuItem>
                              <MenuItem value="under_review">
                                Under Review
                              </MenuItem>
                              <MenuItem value="in_progress">
                                In Progress
                              </MenuItem>
                              <MenuItem value="completed">Completed</MenuItem>
                              <MenuItem value="rejected">Rejected</MenuItem>
                              <MenuItem value="fixed">Fixed</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <Chip
                            label={request.status.replace("_", " ")}
                            size="small"
                            color={getStatusColor(request.status) as any}
                            variant="outlined"
                          />
                        )}
                      </Stack>

                      {editingRequest === request.id ? (
                        <Box sx={{ mb: 2 }}>
                          <TextField
                            fullWidth
                            label="Admin Notes"
                            multiline
                            rows={2}
                            value={editingNotes}
                            onChange={(e) => setEditingNotes(e.target.value)}
                            placeholder="Add notes about this request..."
                            size="small"
                          />
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<Save />}
                              onClick={saveEditing}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<Cancel />}
                              onClick={cancelEditing}
                              sx={{ fontSize: "0.75rem" }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        </Box>
                      ) : (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            <strong>Submitted by:</strong> {request.user_email}
                          </Typography>
                          <br />
                          <Typography variant="caption" color="text.secondary">
                            <strong>Created:</strong>{" "}
                            {new Date(request.created_at).toLocaleDateString()}
                          </Typography>

                          {request.admin_notes && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              <Typography variant="caption">
                                <strong>Admin Notes:</strong>{" "}
                                {request.admin_notes}
                              </Typography>
                            </Alert>
                          )}
                        </>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
        {/* Snackbar for feedback */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default RequestImprovementPage;
